import { findSarpanch } from "./sarpanch"
import { getPanchayatDataProvider } from "./panchayat-data"
import { loadBsData, loadZpData } from "./xlsx-loader"

export type Language = "en" | "hi" | "pa"
export type Level = "gp" | "bs" | "zp"

export interface Selection {
  district: string
  block?: string
  village?: string
}

// Localized person name maps (script translations). Keys are canonical EN names.
import personNamesHi from "./personNames.hi.json" assert { type: "json" }
import personNamesPa from "./personNames.pa.json" assert { type: "json" }
import { latinToDevanagari } from "./transliterate.hi"
import { latinToGurmukhi } from "./transliterate.pa"

// Simple cache to avoid duplicate lookups across selection changes
const cache = new Map<string, string | null>()

function normalize(s: string | null | undefined): string {
  return String(s || "").trim()
}

function cacheKey(level: Level, sel: Selection): string {
  return [level, normalize(sel.district), normalize(sel.block), normalize(sel.village)].join("|")
}

export function isSelectionReady(level: Level, sel: Selection): boolean {
  if (level === "gp") return !!(sel.district && sel.block && sel.village)
  if (level === "bs") return !!(sel.district && sel.block)
  if (level === "zp") return !!sel.district
  return false
}

/**
 * Validate that the current selection exists in the hierarchical dataset.
 * Returns true if the exact path is present; false otherwise.
 */
export async function validateSelectionMatch(level: Level, sel: Selection): Promise<boolean> {
  try {
    const provider = getPanchayatDataProvider()
    const districts = await provider.getDistricts()
    const d = districts.find((entry: any) => matchesByAnyName(entry, sel.district))
    if (!d) return false
    if (level === "zp") return true
    const blocks = await provider.getBlocks(d.id)
    const b = blocks.find((entry: any) => matchesByAnyName(entry, sel.block))
    if (!b) return false
    if (level === "bs") return true
    const villages = await provider.getGramPanchayats(b.id, d.id)
    const v = villages.find((entry: any) => matchesByAnyName(entry, sel.village))
    return !!v
  } catch {
    return false
  }
}

function matchesByAnyName(entity: any, target?: string | null): boolean {
  if (!entity || !target) return false
  const t = normalize(target).toLowerCase()
  const names = entity.names || {}
  const candidates: string[] = [entity.id, entity.code, entity.name, names.en, names.hi, names.pa].filter(Boolean)
  return candidates.some((n) => normalize(String(n)).toLowerCase() === t)
}

/**
 * Resolves the official person name (Sarpanch or Chairperson) for the selection.
 * Currently supports Sarpanch via dataset/XLSX fallback. Chairperson names are
 * returned as null unless an external dataset is later integrated.
 */
export async function resolveOfficialPersonName(level: Level, sel: Selection): Promise<string | null> {
  if (!isSelectionReady(level, sel)) return null
  const key = cacheKey(level, sel)
  if (cache.has(key)) return cache.get(key) ?? null

  try {
    if (level === "gp" && sel.district && sel.block && sel.village) {
      const name = await findSarpanch(sel.district, sel.block, sel.village)
      cache.set(key, name || null)
      return name || null
    }

    if (level === "bs" && sel.district && sel.block) {
      const data = await loadBsData()
      const dKey = normalize(sel.district).toLowerCase()
      const bKey = normalize(sel.block).toLowerCase()
      const item = data[`${dKey}|${bKey}`]
      const name = item?.chairperson?.names?.en || null
      cache.set(key, name)
      return name
    }

    if (level === "zp" && sel.district) {
      const data = await loadZpData()
      const dKey = normalize(sel.district).toLowerCase()
      const item = data[dKey]
      const name = item?.chairperson?.names?.en || null
      cache.set(key, name)
      return name
    }

    cache.set(key, null)
    return null
  } catch (err) {
    cache.set(key, null)
    return null
  }
}

/**
 * Translates a person name to the selected language using structured maps.
 * - If a translation is missing, returns the original name.
 * - For English, returns original name.
 */
export function translatePersonName(name: string | null | undefined, lang: Language): string | null {
  const canonical = normalize(name)
  if (!canonical) return null
  if (lang === "en") return canonical

  const isAllCapsLatin = /^[A-Z0-9\s.'-]+$/.test(canonical) && /[A-Z]/.test(canonical)
  const transliterate = (s: string) => {
    return s.replace(/[A-Za-z][A-Za-z.'-]*/g, (m) => {
      if (lang === "hi") return latinToDevanagari(m)
      if (lang === "pa") return latinToGurmukhi(m)
      return m
    })
  }

  if (lang === "hi") {
    const translated = (personNamesHi as Record<string, string>)[canonical]
    if (translated) return normalize(translated)
    return isAllCapsLatin ? canonical : transliterate(canonical)
  }
  if (lang === "pa") {
    const translated = (personNamesPa as Record<string, string>)[canonical]
    if (translated) return normalize(translated)
    return isAllCapsLatin ? canonical : transliterate(canonical)
  }
  return canonical
}

/**
 * Convenience helper: resolves official name and returns localized form.
 */
export async function getLocalizedPersonName(level: Level, sel: Selection, lang: Language): Promise<string | null> {
  const official = await resolveOfficialPersonName(level, sel)
  return translatePersonName(official, lang)
}
